import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaStar, FaShieldAlt, FaClock, FaCreditCard, FaMobile, FaHeadset, FaGift } from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import SEO from '../components/SEO';

const OfferReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Mock data - in real app, fetch based on id
  const offerData: Record<string, any> = {
    allbritishcasino: {
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100',
      rating: 4.8,
      url: 'https://www.allbritishcasino.com/',
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48 hours',
      license: 'UKGC',
      established: '2013',
      games: 500,
      providers: 20,
      description: 'All British Casino brings a quintessentially British experience to online gaming. With a focus on quality over quantity, this casino offers a curated selection of games from top providers, ensuring every spin and hand delivers excitement.',
      pros: [
        'UKGC licensed and fully regulated',
        'Fast withdrawal processing',
        'Excellent customer support',
        'Wide variety of payment methods',
        'Mobile-optimized platform'
      ],
      cons: [
        'Limited game selection compared to larger casinos',
        'No cryptocurrency payments'
      ],
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer'],
      gameCategories: [
        { name: 'Slots', value: 60, color: '#ff6b35' },
        { name: 'Table Games', value: 20, color: '#f7931e' },
        { name: 'Live Casino', value: 15, color: '#ff8c42' },
        { name: 'Other', value: 5, color: '#4a5568' }
      ],
      monthlyTraffic: [
        { month: 'Jan', visitors: 45000 },
        { month: 'Feb', visitors: 52000 },
        { month: 'Mar', visitors: 48000 },
        { month: 'Apr', visitors: 61000 },
        { month: 'May', visitors: 58000 },
        { month: 'Jun', visitors: 65000 }
      ],
      ratingBreakdown: [
        { category: 'Games', score: 4.7 },
        { category: 'Bonuses', score: 4.9 },
        { category: 'Support', score: 4.8 },
        { category: 'Payouts', score: 4.9 },
        { category: 'Security', score: 5.0 }
      ]
    },
    partycasino: {
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      bonus: '200% up to £200',
      rating: 4.7,
      url: 'https://www.partycasino.com/',
      minDeposit: '£20',
      wagering: '30x',
      payoutTime: '12-24 hours',
      license: 'UKGC',
      established: '1997',
      games: 800,
      providers: 35,
      description: 'PartyCasino is one of the longest-standing names in online gaming. With decades of experience and a reputation for innovation, they continue to deliver exceptional gaming experiences with cutting-edge technology and exclusive titles.',
      pros: [
        'Industry veteran with proven track record',
        'Exclusive game titles',
        'Comprehensive VIP program',
        'Mobile app available',
        'Regular tournaments and promotions'
      ],
      cons: [
        'Higher minimum deposit requirement',
        'Some games restricted in certain regions'
      ],
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer', 'Crypto'],
      gameCategories: [
        { name: 'Slots', value: 65, color: '#ff6b35' },
        { name: 'Table Games', value: 18, color: '#f7931e' },
        { name: 'Live Casino', value: 12, color: '#ff8c42' },
        { name: 'Other', value: 5, color: '#4a5568' }
      ],
      monthlyTraffic: [
        { month: 'Jan', visitors: 78000 },
        { month: 'Feb', visitors: 85000 },
        { month: 'Mar', visitors: 92000 },
        { month: 'Apr', visitors: 88000 },
        { month: 'May', visitors: 95000 },
        { month: 'Jun', visitors: 102000 }
      ],
      ratingBreakdown: [
        { category: 'Games', score: 4.8 },
        { category: 'Bonuses', score: 4.6 },
        { category: 'Support', score: 4.7 },
        { category: 'Payouts', score: 4.8 },
        { category: 'Security', score: 4.9 }
      ]
    },
    plazaroyal: {
      name: 'Plaza Royal',
      image: '/images/offers/plazaroyal.webp',
      bonus: '150% up to £150',
      rating: 4.6,
      url: 'https://www.plazaroyal.com',
      minDeposit: '£15',
      wagering: '40x',
      payoutTime: '24-72 hours',
      license: 'UKGC',
      established: '2018',
      games: 600,
      providers: 25,
      description: 'Plaza Royal combines modern gaming technology with classic casino elegance. This relatively new player in the market has quickly established itself as a favorite among UK players, offering competitive bonuses and a user-friendly platform.',
      pros: [
        'Modern, intuitive interface',
        'Competitive welcome bonus',
        'Accepts cryptocurrency',
        '24/7 customer support',
        'Regular tournament events'
      ],
      cons: [
        'Higher wagering requirements',
        'Longer payout times for some methods'
      ],
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bitcoin', 'Ethereum', 'Bank Transfer'],
      gameCategories: [
        { name: 'Slots', value: 58, color: '#ff6b35' },
        { name: 'Table Games', value: 22, color: '#f7931e' },
        { name: 'Live Casino', value: 15, color: '#ff8c42' },
        { name: 'Other', value: 5, color: '#4a5568' }
      ],
      monthlyTraffic: [
        { month: 'Jan', visitors: 32000 },
        { month: 'Feb', visitors: 38000 },
        { month: 'Mar', visitors: 42000 },
        { month: 'Apr', visitors: 45000 },
        { month: 'May', visitors: 48000 },
        { month: 'Jun', visitors: 52000 }
      ],
      ratingBreakdown: [
        { category: 'Games', score: 4.5 },
        { category: 'Bonuses', score: 4.7 },
        { category: 'Support', score: 4.6 },
        { category: 'Payouts', score: 4.5 },
        { category: 'Security', score: 4.8 }
      ]
    }
  };

  const offer = offerData[id || ''] || offerData.allbritishcasino;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "Organization",
      "name": offer.name
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": offer.rating,
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "ReelVindicator"
    },
    "description": offer.description
  };

  return (
    <>
      <SEO
        title={`${offer.name} Review - ${offer.bonus} Bonus`}
        description={`Complete review of ${offer.name}. ${offer.bonus} welcome bonus, ${offer.rating}/5 rating. Read our expert analysis before you play.`}
        type="article"
        image={offer.image}
        schema={schema}
      />

      <div className="pt-20 pb-12">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-dark-surface to-dark-bg py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full max-w-md mx-auto rounded-xl shadow-2xl"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width="400"
                  height="300"
                />
              </div>
              <div>
                <h1 className="text-5xl font-bold mb-4">
                  <span className="text-gradient">{offer.name}</span> Review
                </h1>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="flex text-accent-primary">
                      {[...Array(5)].map((_, i) => (
                        <FaStar key={i} className={i < Math.floor(offer.rating) ? 'fill-current' : 'text-gray-600'} size={24} />
                      ))}
                    </div>
                    <span className="text-2xl font-bold">{offer.rating}/5</span>
                  </div>
                  <div className="glass px-4 py-2 rounded-lg">
                    <span className="text-accent-primary font-bold">{offer.bonus}</span>
                  </div>
                </div>
                <p className="text-xl text-gray-400 mb-8">{offer.description}</p>
                {offer.url && (
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-4 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all transform hover:scale-105"
                  >
                    Claim Bonus Now
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="glass p-6 rounded-xl text-center">
                <FaGift className="text-3xl text-accent-primary mx-auto mb-2" />
                <div className="text-2xl font-bold mb-1">{offer.bonus}</div>
                <div className="text-gray-400 text-sm">Welcome Bonus</div>
              </div>
              <div className="glass p-6 rounded-xl text-center">
                <FaShieldAlt className="text-3xl text-accent-primary mx-auto mb-2" />
                <div className="text-2xl font-bold mb-1">{offer.license}</div>
                <div className="text-gray-400 text-sm">License</div>
              </div>
              <div className="glass p-6 rounded-xl text-center">
                <FaClock className="text-3xl text-accent-primary mx-auto mb-2" />
                <div className="text-2xl font-bold mb-1">{offer.payoutTime}</div>
                <div className="text-gray-400 text-sm">Payout Time</div>
              </div>
              <div className="glass p-6 rounded-xl text-center">
                <FaCreditCard className="text-3xl text-accent-primary mx-auto mb-2" />
                <div className="text-2xl font-bold mb-1">{offer.minDeposit}</div>
                <div className="text-gray-400 text-sm">Min Deposit</div>
              </div>
            </div>
          </div>
        </section>

        {/* Charts Section */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-8 text-center">
              <span className="text-gradient">Performance Analytics</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Monthly Traffic */}
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">Monthly Traffic Growth</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={offer.monthlyTraffic}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252533" />
                    <XAxis dataKey="month" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a24', border: '1px solid #252533', borderRadius: '8px' }}
                    />
                    <Line type="monotone" dataKey="visitors" stroke="#ff6b35" strokeWidth={3} dot={{ fill: '#ff6b35' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Rating Breakdown */}
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">Rating Breakdown</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={offer.ratingBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252533" />
                    <XAxis dataKey="category" stroke="#9ca3af" />
                    <YAxis domain={[0, 5]} stroke="#9ca3af" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a24', border: '1px solid #252533', borderRadius: '8px' }}
                    />
                    <Bar dataKey="score" fill="#ff6b35" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Game Categories Pie Chart */}
            <div className="glass p-6 rounded-xl max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-4 text-center">Game Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={offer.gameCategories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {offer.gameCategories.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fafafa', border: '1px solid #252533', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-4 text-green-400">✓ Pros</h3>
                <ul className="space-y-3">
                  {offer.pros.map((pro: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold mb-4 text-red-400">✗ Cons</h3>
                <ul className="space-y-3">
                  {offer.cons.map((con: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-red-400 mt-1">✗</span>
                      <span className="text-gray-300">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Methods */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-8 text-center">
              <span className="text-gradient">Payment Methods</span>
            </h2>
            <div className="glass p-6 rounded-xl">
              <div className="flex flex-wrap gap-4">
                {offer.paymentMethods.map((method: string, index: number) => (
                  <div
                    key={index}
                    className="bg-dark-card px-4 py-2 rounded-lg border border-dark-border hover:border-accent-primary transition-colors"
                  >
                    {method}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Review */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-4xl font-bold mb-8 text-center">
              <span className="text-gradient">Detailed Review</span>
            </h2>
            <div className="glass p-8 rounded-xl space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-4">Games & Software</h3>
                <p className="text-gray-400 leading-relaxed">
                  {offer.name} features an impressive collection of {offer.games} games from {offer.providers} leading software providers. 
                  The platform offers a diverse range of slots, table games, and live dealer options, ensuring there's something for every type of player.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">Bonuses & Promotions</h3>
                <p className="text-gray-400 leading-relaxed">
                  New players are welcomed with a generous {offer.bonus} bonus, subject to {offer.wagering} wagering requirements. 
                  The casino also runs regular promotions, tournaments, and loyalty rewards to keep existing players engaged.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">Security & Licensing</h3>
                <p className="text-gray-400 leading-relaxed">
                  Licensed by the {offer.license}, {offer.name} adheres to strict regulatory standards. 
                  The platform uses advanced SSL encryption to protect player data and funds, ensuring a safe and secure gaming environment.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">Customer Support</h3>
                <p className="text-gray-400 leading-relaxed">
                  The support team is available 24/7 via live chat, email, and phone. 
                  Response times are generally quick, and the staff is knowledgeable and helpful in resolving any issues or answering questions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient">Ready to Play?</span>
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Claim your {offer.bonus} bonus and start playing at {offer.name} today!
            </p>
            {offer.url && (
              <a
                href={offer.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-accent-primary to-accent-secondary text-white font-bold py-4 px-8 rounded-lg hover:shadow-lg hover:shadow-accent-primary/50 transition-all transform hover:scale-105"
              >
                Claim Bonus Now
              </a>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default OfferReview;

